import React, { useRef, useState, useEffect } from 'react';
import { getSubtaskStatusBreakdown } from '../../../utils/taskUtils';
import ProgressTooltip from './ProgressTooltip';
import './ProgressIndicator.css';

const TOOLTIP_HOVER_DELAY_MS = 0;
const TOOLTIP_HIDE_DELAY_MS = 150;

// Status color mapping
// Complete color is lighter to match icon appearance with 0.7 opacity
// #0066cc (rgb(0, 102, 204)) at 0.7 opacity over white (255, 255, 255):
// R: 0*0.7 + 255*0.3 = 76.5 ≈ 77 (0x4D)
// G: 102*0.7 + 255*0.3 = 147.9 ≈ 148 (0x94)
// B: 204*0.7 + 255*0.3 = 219.3 ≈ 219 (0xDB)
// Result: #4D94DB
const STATUS_COLORS = {
  'complete': '#4D94DB', // Lighter blue to match complete icon with 0.7 opacity
  'wont_do': '#475569', // Dark blue-gray
  'wip': '#fde68a',
  'waiting': '#e9d5ff',
  'incomplete': '#fff',
  'blocked': '#ffe8e8' // Light pink/red to match blocked icon
};

const STATUS_BORDER_COLORS = {
  'complete': '#0066cc', // Darker blue (original color for border)
  'wont_do': '#334155', // Darker blue-gray
  'wip': '#d97706', // Darker yellow/amber
  'waiting': '#7c3aed', // Darker purple
  'incomplete': '#9ca3af', // Darker gray for white incomplete slices
  'blocked': '#333' // Dark gray/black to match blocked icon border
};

// Check if any subtask has wip or waiting status (recursively)
function getSubtaskStatusInfo(task) {
  if (!task.subtasks || task.subtasks.length === 0) {
    return { color: null, borderColor: null };
  }
  
  let hasWip = false;
  let hasWaiting = false;
  
  const checkSubtasks = (subtask) => {
    if (subtask.status === 'wip') {
      hasWip = true;
    } else if (subtask.status === 'waiting') {
      hasWaiting = true;
    }
    
    if (subtask.subtasks && subtask.subtasks.length > 0) {
      subtask.subtasks.forEach(checkSubtasks);
    }
  };
  
  task.subtasks.forEach(checkSubtasks);
  
  return {
    color: hasWip ? STATUS_COLORS.wip : hasWaiting ? STATUS_COLORS.waiting : null,
    borderColor: hasWip ? STATUS_BORDER_COLORS.wip : hasWaiting ? STATUS_BORDER_COLORS.waiting : null
  };
}

function ProgressIndicator({
  task,
  tasks,
  subtaskStats,
  isSequentialDisabled,
  isBlockingTaskHighlighted,
  isBlockingTaskNotBlocked,
  onBlockedIconMouseEnter,
  onBlockedIconMouseLeave,
  summaryLabel,
  propagateClick = false
}) {
  const anchorRef = useRef(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [emptyClickFeedback, setEmptyClickFeedback] = useState(false);
  const showDelayRef = useRef(null);
  const hideDelayRef = useRef(null);
  const emptyClickTimeoutRef = useRef(null);

  const showTooltip = () => {
    if (showDelayRef.current) {
      clearTimeout(showDelayRef.current);
      showDelayRef.current = null;
    }
    if (hideDelayRef.current) {
      clearTimeout(hideDelayRef.current);
      hideDelayRef.current = null;
    }
    setTooltipVisible(true);
  };

  const hideTooltip = () => {
    if (hideDelayRef.current) {
      clearTimeout(hideDelayRef.current);
      hideDelayRef.current = null;
    }
    setTooltipVisible(false);
  };

  useEffect(() => () => {
    if (showDelayRef.current) clearTimeout(showDelayRef.current);
    if (hideDelayRef.current) clearTimeout(hideDelayRef.current);
    if (emptyClickTimeoutRef.current) clearTimeout(emptyClickTimeoutRef.current);
  }, []);

  if (!subtaskStats || subtaskStats.total === 0) {
    return null;
  }

  const percentage = subtaskStats.total > 0 ? subtaskStats.weightedProgress / subtaskStats.total : 0;
  const percentageText = Math.round(percentage * 100);
  const radius = 9.5;
  const centerX = 10;
  const centerY = 10;
  const baseStartAngle = -90;
  
  // Get status breakdown for rendering multiple slices
  const statusBreakdown = getSubtaskStatusBreakdown(task, tasks || []);
  
  // Helper function to create arc path
  const createArcPath = (startAngle, endAngle) => {
    const startAngleRad = (startAngle * Math.PI) / 180;
    const endAngleRad = (endAngle * Math.PI) / 180;
    
    const startX = centerX + radius * Math.cos(startAngleRad);
    const startY = centerY + radius * Math.sin(startAngleRad);
    const endX = centerX + radius * Math.cos(endAngleRad);
    const endY = centerY + radius * Math.sin(endAngleRad);
    
    const angleDiff = endAngle - startAngle;
    const largeArcFlag = angleDiff > 180 ? 1 : 0;
    
    return [
      `M ${centerX} ${centerY}`,
      `L ${startX} ${startY}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
      'Z'
    ].join(' ');
  };
  
  // Calculate arcs for each status
  let currentAngle = baseStartAngle;
  const arcs = statusBreakdown.map(({ status, percentage: statusPercentage }) => {
    const startAngle = currentAngle;
    const endAngle = currentAngle + (360 * statusPercentage);
    currentAngle = endAngle;
    
    return {
      status,
      startAngle,
      endAngle,
      percentage: statusPercentage,
      pathData: createArcPath(startAngle, endAngle),
      fillColor: STATUS_COLORS[status] || '#767676',
      borderColor: STATUS_BORDER_COLORS[status] || '#767676'
    };
  });
  
  // Get overall status color info for border
  const statusInfo = getSubtaskStatusInfo(task);
  const overallBorderColor = statusInfo.borderColor;
  const overallProgressColor = statusInfo.color;
  
  // Check if all subtasks have the same status (and it's not incomplete or blocked)
  // Blocked status should always show as slices to be more informative
  const allSameStatus = arcs.length === 1 && arcs[0].status !== 'incomplete' && arcs[0].status !== 'blocked';
  const shouldShowFilledCircle = allSameStatus && !isSequentialDisabled;

  const handleMouseEnter = () => {
    if (isSequentialDisabled && onBlockedIconMouseEnter) {
      onBlockedIconMouseEnter(task._reactKey);
    }
    if (hideDelayRef.current) {
      clearTimeout(hideDelayRef.current);
      hideDelayRef.current = null;
    } else if (!tooltipVisible) {
      showDelayRef.current = setTimeout(showTooltip, TOOLTIP_HOVER_DELAY_MS);
    }
  };

  const handleMouseLeave = () => {
    if (isSequentialDisabled && onBlockedIconMouseLeave) {
      onBlockedIconMouseLeave();
    }
    if (showDelayRef.current) {
      clearTimeout(showDelayRef.current);
      showDelayRef.current = null;
    }
    if (tooltipVisible) {
      hideDelayRef.current = setTimeout(hideTooltip, TOOLTIP_HIDE_DELAY_MS);
    }
  };

  const handleClick = (e) => {
    if (propagateClick) return;
    e.preventDefault();
    e.stopPropagation();
    if (percentage === 0 && !isSequentialDisabled) {
      if (emptyClickTimeoutRef.current) clearTimeout(emptyClickTimeoutRef.current);
      setEmptyClickFeedback(true);
      emptyClickTimeoutRef.current = setTimeout(() => {
        setEmptyClickFeedback(false);
        emptyClickTimeoutRef.current = null;
      }, 400);
    }
  };

  const handleTooltipMouseEnter = () => {
    if (hideDelayRef.current) {
      clearTimeout(hideDelayRef.current);
      hideDelayRef.current = null;
    }
  };

  const handleTooltipMouseLeave = () => {
    hideDelayRef.current = setTimeout(hideTooltip, TOOLTIP_HIDE_DELAY_MS);
  };

  return (
    <div 
      ref={anchorRef}
      className={`progress-indicator ${isSequentialDisabled ? 'progress-indicator-disabled' : ''} ${emptyClickFeedback ? 'progress-indicator-empty-click' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={propagateClick ? undefined : handleClick}
    >
      <div className="progress-circle">
        <svg className="progress-circle-svg" viewBox="0 0 20 20">
          {shouldShowFilledCircle ? (
            // All subtasks have the same status - show single filled circle
            <circle
              className="progress-circle-background"
              cx={centerX}
              cy={centerY}
              r={radius}
              fill={arcs[0].fillColor}
              stroke={isSequentialDisabled ? "#333" : "#555"}
              strokeWidth={isBlockingTaskHighlighted ? "1.5" : "0.8"}
            />
          ) : (
            <>
              <circle
                className="progress-circle-background"
                cx={centerX}
                cy={centerY}
                r={radius}
                fill={isSequentialDisabled ? "#ffe8e8" : "#fff"}
                stroke="none"
                strokeWidth="0"
              />
              {arcs.map((arc, index) => {
                // Always show blocked arcs
                // Show other arcs only when percentage > 0 and not sequential disabled
                const shouldShow = arc.status === 'blocked' || (!isSequentialDisabled && percentage > 0);
                if (!shouldShow) return null;
                
                return (
                  <path
                    key={`${arc.status}-${index}`}
                    className="progress-circle-fill"
                    d={arc.pathData}
                    fill={arc.fillColor}
                    stroke={arc.borderColor}
                    strokeWidth="0.8"
                  />
                );
              })}
              {/* Outer border circle - rendered last to always be on top */}
              <circle
                className="progress-circle-border"
                cx={centerX}
                cy={centerY}
                r={radius}
                fill="none"
                stroke={isSequentialDisabled ? "#333" : "#555"}
                strokeWidth={isBlockingTaskHighlighted ? "1.5" : "0.8"}
              />
            </>
          )}
          {isSequentialDisabled && (
            <line
              x1="5.5"
              y1="10"
              x2="14.5"
              y2="10"
              stroke="#555"
              strokeWidth="2"
              strokeLinecap="round"
              className="progress-circle-disabled-line"
            />
          )}
        </svg>
      </div>
      <ProgressTooltip
        visible={tooltipVisible}
        anchorRef={anchorRef}
        task={task}
        tasks={tasks}
        subtaskStats={subtaskStats}
        onMouseEnter={handleTooltipMouseEnter}
        onMouseLeave={handleTooltipMouseLeave}
        summaryLabel={summaryLabel}
      />
    </div>
  );
}

export default ProgressIndicator;

